import {
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  FormControl,
  FormLabel,
  Stack,
  Switch,
} from '@chakra-ui/react';
import { useFetchAfesPageQuery, useFetchUsersPageQuery } from '../../api';
import { useAuth } from '../../core';
import { AppInput } from '../components';
import {
  AfeAutoComplete,
  FormButtonGroup,
  UserAutoComplete,
} from '../components';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { PutPurchaseOrderRequest } from '../../api/bindings/PutPurchaseOrderRequest';
import { PurchaseOrderResponse } from 'api/bindings/PurchaseOrderResponse';
import { DatePicker } from 'features';
import { PurchaseOrderSearchRecordResponse } from 'api/bindings/PurchaseOrderSearchRecordResponse';

export interface PurchaseOrderUpdateFormDrawerContentProps
  extends Omit<DrawerContentProps, 'onSubmit' | 'children'> {
  purchaseOrder: PurchaseOrderResponse | PurchaseOrderSearchRecordResponse;
  onSubmit: (
    form: PutPurchaseOrderRequest & { includeRevision: boolean }
  ) => void;
  onCancel: () => void;
}

interface Form extends PutPurchaseOrderRequest {
  includeRevision: boolean;
}

export const PurchaseOrderUpdateFormDrawerContent = (
  props: PurchaseOrderUpdateFormDrawerContentProps
) => {
  const { purchaseOrder, onSubmit, onCancel, ...rest } = props;

  // Fetches
  const auth = useAuth();
  const { data: afes } = useFetchAfesPageQuery(
    {
      clientId: auth?.user.clientId ?? '0',
      all: true,
    },
    { skip: !auth?.user.clientId }
  );
  const { data: users } = useFetchUsersPageQuery(
    {
      clientId: auth?.user.clientId ?? '0',
      all: true,
    },
    { skip: !auth?.user.clientId }
  );

  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      id: purchaseOrder.id,
      assigneeUserId: purchaseOrder.assigneeUserId as any,
      name: purchaseOrder.name,
      description: purchaseOrder.description,
      commitmentDate: purchaseOrder.commitmentDate,
      reference: purchaseOrder.reference,
      lineItems:
        'lineItems' in purchaseOrder ? purchaseOrder.lineItems : ([] as any),
      includeRevision: false,
    },
  });
  const { control, register, formState, watch, handleSubmit } = form;

  const header = watch('name');

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DrawerContent {...rest}>
          <DrawerCloseButton />
          <DrawerHeader>Update Purchase Order: {header}</DrawerHeader>
          <DrawerBody>
            <Stack spacing={4}>
              {/* <FormControl>
                <FormLabel htmlFor="afe">AFE:</FormLabel>
                <Controller
                  name="afeId"
                  control={control}
                  render={(p) => (
                    <AfeAutoComplete
                      id="afe"
                      items={afes?.items}
                      {...p.field}
                      isDisabled={true}
                      value={purchaseOrder.afeId}
                    />
                  )}
                />
              </FormControl> */}
              <FormControl>
                <FormLabel htmlFor="name">Name:</FormLabel>
                <AppInput
                  id="name"
                  {...register('name', {
                    minLength: {
                      value: 3,
                      message: 'Name must be between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message: 'Name must be between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="description">Description:</FormLabel>
                <AppInput
                  id="description"
                  {...register('description', {
                    minLength: {
                      value: 3,
                      message:
                        'Description must be between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message:
                        'Description must be between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="asigneeUser">Asignee:</FormLabel>
                <Controller
                  name="assigneeUserId"
                  control={control}
                  render={(p) => (
                    <UserAutoComplete
                      id="asigneeUser"
                      items={users?.items}
                      {...p.field}
                      isDisabled={true}
                      value={purchaseOrder.assigneeUserId}
                    />
                  )}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="commitmentDate">Commitment Date:</FormLabel>
                <DatePicker name="commitmentDate" control={control} />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="reference">Reference:</FormLabel>
                <AppInput
                  id="reference"
                  {...register('reference', {
                    minLength: {
                      value: 3,
                      message: 'Reference must be between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message: 'Reference must be between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="includeRevision">
                  Include a Revision?
                </FormLabel>
                <Controller
                  name="includeRevision"
                  control={control}
                  render={(p) => (
                    <Switch
                      size="lg"
                      isChecked={p.field.value}
                      onChange={() => p.field.onChange(!p.field.value)}
                    />
                  )}
                />
              </FormControl>
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              onCancel={onCancel}
              onConfirm={handleSubmit(onSubmit)}
            />
          </DrawerFooter>
        </DrawerContent>
      </form>
    </FormProvider>
  );
};
